import React from 'react';
import { NoData, Poppins, Spacer, TableSkeleton } from 'src/common';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { useColumnsControls } from './useColumnsControls';
import GenericTable from 'src/components/GenericTable';
import useVersion from 'src/hooks/useVersion';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { AiSuggest } from '../../../comps/AiSuggest';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { isQueryLoading, useCache } from 'src/graphql';
import { ScenarioQuery, useScenarioControlsQuery } from 'src/graphql/_generated';
import { RiskManagementModal } from 'src/components/modals';
import { ScenarioControlExpandContent } from './ScenarioControlExpandContent';

const Div = styled.div`
  .head {
    display: flex;
    justify-content: space-between;
  }

  .empty-label {
    font-size: 18px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }

  .error-label {
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.gp1};
  }
`;

interface ControlsProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
}

export const Controls: React.FC<ControlsProps> = ({ scenario }) => {
  const { version } = useVersion();
  const { setLinkControlsToScenarioModal } = useActionsApp();
  const { refetchQuery } = useCache();

  const {
    data: scenarioControlsData,
    loading,
    networkStatus,
    error,
  } = useScenarioControlsQuery({
    variables: {
      id: scenario.id,
      versionId: version,
    },
  });

  const isLoading = isQueryLoading({
    loading,
    data: scenarioControlsData,
    networkStatus,
  });

  const riskManagementPlans = scenarioControlsData?.scenario?.riskManagementPlans || [];

  const { columns, GTColumnSelectAnchorExported, editModalId, setEditModalId } = useColumnsControls({
    data: riskManagementPlans,
    scenario: scenario,
  });

  const resetCache = async () => refetchQuery({ include: ['scenario', 'scenarioControls', 'scenarioRmp'] });

  const isSuggestionButtonReadOnly = !!scenario.riskManagementPlans?.length;

  return (
    <>
      {editModalId && (
        <RiskManagementModal
          onClose={() => {
            resetCache();
            setEditModalId(undefined);
          }}
          controlId={editModalId}
        />
      )}
      <Div>
        <div className="head">
          <Poppins weight={600}>RISK MANAGEMENT PLAN</Poppins>
          {!version && (
            <Button
              primary
              onClick={() => {
                setLinkControlsToScenarioModal(true);
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Add controls',
                  tags: ['SCENARIO', 'RMP'],
                });
              }}
              data-cy="add-controls-btn"
              $constWidth={200}
            >
              + ADD CONTROLS
            </Button>
          )}
        </div>
        {!isLoading ? (
          <>
            {!isSuggestionButtonReadOnly && (
              <AiSuggest
                wrapperStyle={{
                  marginTop: -10,
                  marginBottom: !!riskManagementPlans.length ? 4 : 10,
                }}
                scenarioId={scenario.id}
              />
            )}
            {riskManagementPlans.length ? (
              <>
                <Spacer $px={10} />
                <GenericTable
                  tableId={TableIds.scenarioControls}
                  data={riskManagementPlans}
                  columns={columns}
                  GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                  searchable={[
                    'strategy',
                    'name',
                    'owner',
                    'frameworkLibrary_shortname',
                    'controlId',
                    'status',
                    'updated_at',
                  ]}
                  expandContent={(rmp) => {
                    if (!rmp.control) return null;
                    return <ScenarioControlExpandContent scenario={scenario} controlId={rmp.control.id} />;
                  }}
                  hideExpandContentWhenClosed
                />
              </>
            ) : (
              <>
                <Spacer $px={8} />
                {error ? <span className="error-label">Server error</span> : <NoData title="No controls linked" />}
              </>
            )}
          </>
        ) : (
          <TableSkeleton />
        )}
      </Div>
    </>
  );
};
